import { LevelSelectionScene } from "./LevelSelectionScene";
import { GameConstants } from "../../GameConstants";
import { GameVars } from "../../GameVars";
import { RavalButton, AudioButton } from "../../utils/Utils";
import { GameManager } from "../../GameManager";
import { DottySlider } from "../../utils/DottySlider";

export class LevelSelectionGUI extends Phaser.GameObjects.Container {

    public ignoreLoadLevel: boolean = false;

    public nextPageButton: RavalButton;
    public prevPageButton: RavalButton;

    public nextCategoryButton: Phaser.GameObjects.Image;
    public prevCategoryButton: Phaser.GameObjects.Image;
    public dottySlider: DottySlider;

    private levelSelectionScene: LevelSelectionScene;
    private backgroundImage: Phaser.GameObjects.Image;
    private bottomBar: Phaser.GameObjects.Image;
    private categoryText: Phaser.GameObjects.Text;
    private dotIndicatorBarBackground: Phaser.GameObjects.Graphics;
    private homeButton: RavalButton;
    private muteButton: AudioButton;

    // Variables for generating the scene 
    private bottomArrowPadding: number;
    private topArrowPadding: number = 0;
    private pageBarHeight: number = 0;
    private dotBarHeight: number = 0;
    private topButtonsOffset: number = 0;

    private initSwipeX: number = 0;
    private swapDistance: number = 0;
    private mouseDown: boolean = false;

    constructor(scene: LevelSelectionScene) {

        super(scene);
        this.levelSelectionScene = scene;
        this.create();
    }

    public create(): void {

        // Set variables 
        this.bottomArrowPadding = 50;
        this.topArrowPadding = 20;
        this.pageBarHeight = 50;
        this.topButtonsOffset = 10;
        this.dotBarHeight = 45;

        this.scaleX = GameVars.scaleXMult;
        this.scaleY = GameVars.scaleYMult;

        // Create Background Images
        this.backgroundImage = new Phaser.GameObjects.Image(this.levelSelectionScene, 0, 0, "texture_atlas_1", "background.png");
        this.backgroundImage.setDisplaySize(GameConstants.GAME_WIDTH * GameVars.scaleXMultInverse, GameConstants.GAME_HEIGHT * GameVars.scaleYMultInverse);
        this.backgroundImage.setOrigin(0);
        this.add(this.backgroundImage);

        // Create bottom bar 
        this.bottomBar = new Phaser.GameObjects.Image(this.levelSelectionScene, 0, 0, "texture_atlas_1", "bar_menu.png");
        this.bottomBar.setPosition(0, (GameConstants.GAME_HEIGHT * GameVars.scaleYMultInverse) - this.bottomBar.height);
        this.bottomBar.setDisplaySize((GameConstants.GAME_WIDTH * GameVars.scaleXMultInverse), this.bottomBar.height);
        this.bottomBar.setOrigin(0);
        this.add(this.bottomBar);

        // Create Items for bottom bar -------------------------------------------

        // Create Icon for category          
        this.categoryText = new Phaser.GameObjects.Text(LevelSelectionScene.currentInstance, 0, 0, GameVars.currentDrawCategory, {
            fontFamily: "FredokaOne",
            fontSize: 75,
            color: "rgb(39, 118, 164)"
        });

        let textOfsset: number = this.bottomBar.height / 10;

        this.categoryText.setPosition((GameConstants.GAME_WIDTH * GameVars.scaleXMultInverse) / 2, (GameConstants.GAME_HEIGHT * GameVars.scaleYMultInverse) - this.bottomBar.height / 2 + textOfsset);
        this.categoryText.setOrigin(.5);

        this.add(this.categoryText);

        // Create Arrow for swaping between categories 
        if (this.scene.sys.game.device.os.desktop) {
            this.prevCategoryButton = new RavalButton(LevelSelectionScene.currentInstance, 0, 0, "texture_atlas_1", "btn_arrow.png", "btn_arrow_over.png");
        } else {
            this.prevCategoryButton = new RavalButton(LevelSelectionScene.currentInstance, 0, 0, "texture_atlas_1", "btn_arrow.png");
        }

        this.prevCategoryButton.setPosition(this.bottomArrowPadding, GameConstants.GAME_HEIGHT * GameVars.scaleYMultInverse - this.prevCategoryButton.height / 2 - textOfsset);
        this.prevCategoryButton.setInteractive();
        this.prevCategoryButton.setFlip(true, false);
        this.prevCategoryButton.setOrigin(.5);

        this.add(this.prevCategoryButton);

        // Create Callbacks
        this.prevCategoryButton.on("pointerdown", function () {
            LevelSelectionScene.currentInstance.levelSelectionManager.goPrevCategory();

            this.refreshDottySlider();

            if (!this.scene.sys.game.device.os.desktop) {
                this.prevCategoryButton.setTexture("texture_atlas_1", "btn_arrow_over.png");
            }

        }, this);

        // Create Arrow for swaping between categories 
        if (this.scene.sys.game.device.os.desktop) {
            this.nextCategoryButton = new RavalButton(LevelSelectionScene.currentInstance, 0, 0, "texture_atlas_1", "btn_arrow.png", "btn_arrow_over.png");
        } else {
            this.nextCategoryButton = new RavalButton(LevelSelectionScene.currentInstance, 0, 0, "texture_atlas_1", "btn_arrow.png");
        }

        this.nextCategoryButton.setPosition(GameConstants.GAME_WIDTH * GameVars.scaleXMultInverse - this.bottomArrowPadding, this.prevCategoryButton.y);
        this.nextCategoryButton.setInteractive();
        this.nextCategoryButton.setOrigin(.5);

        this.add(this.nextCategoryButton);

        // Create Callbacks
        this.nextCategoryButton.on("pointerdown", function () {
            LevelSelectionScene.currentInstance.levelSelectionManager.goNextCategory();

            this.refreshDottySlider();

            if (!this.scene.sys.game.device.os.desktop) {
                this.nextCategoryButton.setTexture("texture_atlas_1", "btn_arrow_over.png");
            }

        }, this);

        // -----------------------------------------------------------------------------

        // Create dot indicator for pages ---------------------------------------------- 

        this.dotIndicatorBarBackground = new Phaser.GameObjects.Graphics(this.levelSelectionScene);
        this.dotIndicatorBarBackground.fillStyle(new Phaser.Display.Color(15, 47, 94).color, 0.8);
        this.dotIndicatorBarBackground.fillRect(0, 0, GameConstants.GAME_WIDTH * GameVars.scaleXMultInverse, this.dotBarHeight);
        this.dotIndicatorBarBackground.setPosition(0, this.bottomBar.y - this.dotBarHeight * .5);
        this.add(this.dotIndicatorBarBackground);

        this.refreshDottySlider();

        // ----------------------------------------------------------------------------- 

        if (this.scene.sys.game.device.os.desktop) {

            // Create arrow for prev page
            this.prevPageButton = new RavalButton(LevelSelectionScene.currentInstance, 0, 0, "texture_atlas_1", "btn_page.png", "btn_page_over.png");
            this.prevPageButton.setPosition(0, (GameConstants.GAME_HEIGHT * GameVars.scaleYMult) - this.prevPageButton.height - this.bottomBar.height + 10);
            this.prevPageButton.setFlip(true, false);
            this.prevPageButton.setOrigin(0);

            this.add(this.prevPageButton);

            this.prevPageButton.onUp(function () {

                LevelSelectionScene.currentInstance.levelSelectionManager.goPrevPage();

            }, this);

            //  Create Arrow for next page 
            this.nextPageButton = new RavalButton(LevelSelectionScene.currentInstance, 0, 0, "texture_atlas_1", "btn_page.png", "btn_page_over.png");
            this.nextPageButton.setPosition((GameConstants.GAME_WIDTH * GameVars.scaleXMult) - this.nextPageButton.width, this.prevPageButton.y);
            this.nextPageButton.blendMode = 4;
            this.nextPageButton.setOrigin(0);

            this.add(this.nextPageButton);

            this.nextPageButton.onUp(function () {
                LevelSelectionScene.currentInstance.levelSelectionManager.goNextPage();
            }, this);
        }

        // Create home button 
        if (this.scene.sys.game.device.os.desktop) {

            this.homeButton = new RavalButton(this.scene, 0, 0, "texture_atlas_1", "btn_home.png", "btn_home_over.png");
            this.homeButton.setOrigin(0);
            this.homeButton.setPosition(this.topButtonsOffset, this.topButtonsOffset);

            this.homeButton.onUp(function () {
                this.scene.scene.start("HomeScene");
            }, this);

        } else {

            this.homeButton = new RavalButton(this.scene, 0, 0, "texture_atlas_1", "btn_home.png", "btn_home_over.png");

            this.homeButton.setOrigin(0);
            this.homeButton.setPosition(this.topButtonsOffset, this.topButtonsOffset);

            this.homeButton.onDown(function () {
                this.homeButton.setTexture("texture_atlas_1", "btn_home_over.png");
            }, this);

            this.homeButton.onUp(function () {
                this.scene.scene.start("HomeScene");
            }, this);
        }


        this.add(this.homeButton);

        // Create mute button    
        let stateArray = ["btn_music_on.png", "btn_music_off.png"];
        let stateArrayOver = ["btn_music_on_over.png", "btn_music_off_over.png"];

        this.muteButton = new AudioButton(this.scene);
        this.muteButton.setPosition((GameConstants.GAME_WIDTH - this.topButtonsOffset) * GameVars.scaleXMultInverse, this.topButtonsOffset * GameVars.scaleYMultInverse);
        this.add(this.muteButton);

        // Swiping 
        this.scene.input.on("pointerdown", function (pointerPos: Phaser.Geom.Point) {
            this.initSwipeX = pointerPos.x;
            this.mouseDown = true;

        }, this);

        this.scene.input.on("pointermove", function (pointerPos: Phaser.Geom.Point) {

            if (!this.mouseDown) {
                return;
            }

            this.swapDistance = this.initSwipeX - pointerPos.x;
            let distanceAbs = Math.abs(this.swapDistance);

            if (distanceAbs > 100 && this.ignoreLoadLevel === false) {
                this.ignoreLoadLevel = true;
            }

        }, this);

        this.scene.input.on("pointerup", function (pointerPos: Phaser.Geom.Point) {

            if (!this.scene.sys.game.device.os.desktop) {
                this.nextCategoryButton.setTexture("texture_atlas_1", "btn_arrow.png");
                this.prevCategoryButton.setTexture("texture_atlas_1", "btn_arrow.png");
            }

            this.initSwipeX = pointerPos.x;

            if (this.ignoreLoadLevel) {

                if (this.swapDistance > 0) {
                    this.levelSelectionScene.levelSelectionManager.goNextPage();
                } else {
                    this.levelSelectionScene.levelSelectionManager.goPrevPage();
                }
            }

            this.mouseDown = false;
            this.ignoreLoadLevel = false;

        }, this);
    }

    public setCategoryText(category: string): void {

        this.categoryText.setText(category);
    }

    private refreshDottySlider(): void {

        if (this.dottySlider) {
            this.dottySlider.destroy();
        }

        this.dottySlider = new DottySlider(this.levelSelectionScene, 0, 0, !this.scene.sys.game.device.os.desktop);
        this.dottySlider.setPosition(GameConstants.GAME_WIDTH * GameVars.scaleXMultInverse / 2, this.dotIndicatorBarBackground.y + (this.dotBarHeight * .5));
        this.dottySlider.onCategoryChanged(this.getPagesAmmountInCategory(GameVars.currentDrawCategory), GameVars.currentCategoryPage);
        this.add(this.dottySlider);
    }

    private getPagesAmmountInCategory(categoryName: string): number {

        let catAmmount: number = 0;

        switch (categoryName) {

            case "ANIMALS":
                catAmmount = GameVars.animalAmount;
                break;

            case "FOOD":
                catAmmount = GameVars.foodAmount;
                break;

            case "HUMANS":
                catAmmount = GameVars.peopleAmount;
                break;


            case "STUFF":
                catAmmount = GameVars.stuffAmount;
                break;

            case "MONSTERS":
                catAmmount = GameVars.monstersAmount;
                break;

            case "PLANTS":
                catAmmount = GameVars.plantsAmount;
                break;

            case "VEHICLES":
                catAmmount = GameVars.vehiclesAmount;
                break;

            default:
                break;
        }

        let ammount = catAmmount / GameConstants.ITEMS_PER_PAGE;

        if (ammount % 1 !== 0) {
            ammount++;
        }

        return Phaser.Math.FloorTo(ammount);
    }
}

